import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import Tools from 'components/userPanel/Tools';
import UserSettings from 'components/_common/UserSettings';
import PanelLayout from 'components/_layouts/panel/PanelLayout';
import { border, colors } from 'theme';
import { usersApi } from 'api';
import { MenuItemExt, PanelMenu } from 'components/_common/PanelMenu';
import { Pair } from 'types/Pair';
import ReportIssues from 'components/_common/ReportIssues';
import ChangeOrganization from 'components/_common/panel/ChangeOrganization';
import { useScope } from 'contexts/scopeContext';
import { useGlobal } from 'contexts/globalContext';
import { useNavigate } from 'hooks/useNavigate';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'components/_new/Button';
import { Divider } from 'components/_new/Divider';
import { useAuth } from 'contexts/authContext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'components/_new/Dialog';
import { MyAccountDialog } from 'components/myAccount/MyAccountDialog';
import { MyCalendarDialog } from 'components/myCalendar/MyCalendarDialog';
import { ChangeAvatarDialog } from 'components/myAccount/components/ChangeAvatarDialog';
import { ChatButton } from 'components/chat/ChatButton';
import { surveyApi } from 'api/survey';
import { FinalSurveyPanel } from 'components/finalSurveyPage/FinalSurveyPanel';
import { userDisplayName } from 'utils/userDisplayName';
import { Icon } from 'components/_new/Icon';
import { useKnowledgeBaseArticleLink } from 'components/knowledgeBase/KnowledgeBaseLink';
import { KnowleedgeBaseContextProvider } from 'components/knowledgeBase/knowledgeBaseContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { ProgramFinishedPage } from '../ProgramFinishedPage';
import { WelcomeDialog } from './components/WelcomeDialog';
import { Link as NewLink } from '../_new/Link';
import { ReactComponent as HomeSvg } from '../../assets/icons/new/home.svg';
import { ReactComponent as UsersSvg } from '../../assets/icons/new/users.svg';
import { ReactComponent as CalendarFullSvg } from '../../assets/icons/new/calendar-full.svg';
import { ReactComponent as FileSignSvg } from '../../assets/icons/new/file-sign.svg';
import { ReactComponent as FileTextWriteSvg } from '../../assets/icons/new/file-text-write.svg';
import { ReactComponent as FileTextSvg } from '../../assets/icons/new/file-text.svg';
import { ReactComponent as FileChecklistSvg } from '../../assets/icons/new/file-checklist.svg';
import { ReactComponent as ChecklistSvg } from '../../assets/icons/new/checklist.svg';
import { ReactComponent as FileCheckSvg } from '../../assets/icons/new/file-check.svg';
import { ReactComponent as PapersSvg } from '../../assets/icons/new/papers.svg';
import { ReactComponent as IdentifierSvg } from '../../assets/icons/new/identifier.svg';
import { ReactComponent as BookSvg } from '../../assets/icons/new/book.svg';
import { ReactComponent as UserSvg } from '../../assets/icons/new/user.svg';
import { ReactComponent as CalendarDateSvg } from '../../assets/icons/new/calendar-date.svg';
import { ReactComponent as FileEdit } from '../../assets/icons/new/file-edit.svg';
import { ReactComponent as SettingsSvg } from '../../assets/icons/new/settings.svg';
import { ReactComponent as CommunicationSvg } from '../../assets/icons/new/communication.svg';
import { ReactComponent as DesctopSvg } from '../../assets/icons/new/desctop.svg';
import { ReactComponent as HandSvg } from '../../assets/icons/new/hand.svg';
import { ReactComponent as BrandLinkedInSvg } from '../../assets/icons/new/brand-linkedin.svg';

const UserPanelLayoutInner = () => {
	const { t, i18n } = useTranslation();
	const { organizationName, programName, pairId } = useParams();
	const navigate = useNavigate({
		scroll: ['scrollWrapperRef', 'pageScrollWrapperRef'],
	});

	const { currentUser, currentUserRefetch } = useAuth();
	const { currentProgram } = useScope();
	const { panelType, canSwitchPanels, userPairs, panelScopePath, currentProgramMembership, todosData } = usePanel();
	const [showOrgDialog, setShowOrgDialog] = useState(false);
	const [visibleLeft, setVisibleLeft] = useState(false);
	const { hasPanelAccess } = useScope();
	const {
		toastRef,
		reportIssueModalIsOpen,
		setReportIssueModalIsOpen,
		myAccountModalOpen,
		setMyAccountModalOpen,
		myCalendarModalOpen,
		setMyCalendarModalOpen,
		avatarModalIsOpen,
		setAvatarModalIsOpen,
	} = useGlobal();

	useEffect(() => {
		if (hasPanelAccess === false) {
			navigate('/');
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: t('misc.error'),
				detail: 'Aplikacja wymaga akceptacji administratora programu',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasPanelAccess]);

	if (!currentProgram) {
		return null;
	}

	if (currentProgram.finished) {
		return <ProgramFinishedPage nextProgramName={currentProgram.nextProgramName} />;
	}

	const { link } = useKnowledgeBaseArticleLink('mentor-development');

	const { displayName, subtitle, logotype, language, pairingStatus } = currentProgram;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const mapPairMenuItem = (pair: Pair, currentIndex: number) => {
		const { menteeApplication, mentorApplication, id } = pair;

		const menteeUser = menteeApplication?.programMembership?.user || null;
		const mentorUser = mentorApplication?.programMembership?.user || null;

		const label = menteeUser ? userDisplayName(menteeUser) : userDisplayName(mentorUser);

		const lastSessionId = pair?.stats?.lastSessionId || null;

		const evaluationLink = lastSessionId
			? `${panelScopePath}/pair/${id}/session/${lastSessionId}/evaluation-mentee`
			: null;
		const pairTodos =
			todosData.pair.find((pairTodo) => pairTodo.items.some((item) => item.variables?.pairId === id))?.items ||
			[];

		const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
		const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
		const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');
		const hasFinalReportAlert = pairTodos.some((todo) => todo.type === `final_report_mentor`);
		const hasEvaluationAlert = pairTodos.some((todo) => todo.type === 'evaluation_survey_required');
		return {
			id: `pair-${id}`,
			label,
			expanded: Boolean(Number(pairId) === pair.id) || (!pairId && currentIndex === 0),
			items: [
				{
					id: `pair-${id}-session`,
					icon: <CalendarFullSvg />,
					label: t('userPanel.navigation.sessions'),
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/session`),
					badge: hasSessionAlert ? (
						<Icon
							name="exclamation-circle"
							iconStyle="solid"
							className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
						/>
					) : null,
				},
				{
					id: `pair-${id}-contract`,
					icon: <FileSignSvg />,
					label: t('userPanel.navigation.contract'),
					badge: hasContractAlert ? (
						<Icon
							name="exclamation-circle"
							iconStyle="solid"
							className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
						/>
					) : null,
					command: () => {
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/contract`);
					},
				},
				{
					id: `pair-${id}-mentee-plan`,
					icon: <FileTextWriteSvg />,
					label: t('userPanel.navigation.mentoringPlan'),
					badge: hasMenteePlanAlert ? (
						<Icon
							name="exclamation-circle"
							iconStyle="solid"
							className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
						/>
					) : null,
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/mentee-plan`),
				},
				panelType === 'mentor' &&
					currentUser?.isProfessionalViewEnabled && {
						id: `pair-${id}-mentor-plan`,
						icon: <FileTextSvg />,
						label: t('userPanel.navigation.mentorPlan'),
						command: () => navigate(String(link?.to?.pathname)),
					},
				{
					separator: true,
				},
				{
					id: `pair-${id}-final-report`,
					icon: <FileChecklistSvg />,
					label: t('userPanel.navigation.finalReport'),
					badge: hasFinalReportAlert ? (
						<Icon
							name="exclamation-circle"
							iconStyle="solid"
							className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
						/>
					) : null,
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/final-report`),
				},
				evaluationLink
					? {
							id: `pair-${id}-evaluation`,
							icon: <ChecklistSvg />,
							label: t('userPanel.navigation.evaluation'),
							badge: hasEvaluationAlert ? (
								<Icon
									name="exclamation-circle"
									iconStyle="solid"
									className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
								/>
							) : null,
							command: () =>
								navigate({
									pathname: evaluationLink,
									hash: '#content',
								}),
					  }
					: null,
				{
					id: `pair-${id}-process-summary`,
					icon: <FileCheckSvg />,
					label: t('userPanel.navigation.processSummary'),
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/process-summary`),
				},
				{
					separator: true,
				},
				{
					id: `pair-${id}-materials`,
					icon: <PapersSvg />,
					label: t('userPanel.navigation.userFiles'),
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/userfiles`),
				},
				{
					id: `pair-${id}-mentors`,
					icon: <IdentifierSvg />,
					label:
						pair.application.applicationRole === 'mentee'
							? t('userPanel.navigation.pairDataForMentees')
							: t('userPanel.navigation.pairDataForMentors'),
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/pair-details`),
				},
			].filter(Boolean) as MenuItemExt[],
		};
	};

	const pairsMenu = useMemo(() => {
		if ((!userPairs || userPairs.length === 0) && !pairingStatus) {
			return [
				{
					id: 'no-pairs',
					label: t('userPanel.navigation.noPairs', 'No pairs yet'),
					disabled: true,
					icon: <Icon name="hourglass" iconStyle="regular" className="fa-xs pr-2" />,
					tooltip: t(
						'userPanel.navigation.noPairsTooltip',
						'Please wait for pairing to be enabled or for the program organizers to create pairs.'
					),
				},
			];
		}

		return (userPairs || []).map(mapPairMenuItem);
	}, [mapPairMenuItem, userPairs, t]);

	const browseMembersMentorAccess = currentProgram.browseMembersByMentor && panelType === 'mentor';
	const browseMembersMenteeAccess = currentProgram.browseMembersByMentee && panelType === 'mentee';

	const linkedInBadgeEnabled =
		(currentProgram.mentorLabelKnowledgeBaseArticleId && panelType === 'mentor') ||
		(currentProgram.menteeLabelKnowledgeBaseArticleId && panelType === 'mentee');

	const { eventsEnabled } = currentProgram;

	const navigationOptions: MenuItemExt[] = useMemo(
		() =>
			[
				{
					id: 'dashboard',
					icon: <HomeSvg />,
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}`),
					label: t('userPanel.navigation.dashboard'),
					divider: true,
				},
				{
					id: 'divider1',
					className: 'p-panelmenu-divider',
				},
				{
					id: 'pairing',
					icon: <UsersSvg />,
					label: t('userPanel.navigation.pairing'),
					expanded: true,
					items: [
						...pairsMenu,
						currentProgram.pairingStatus && {
							id: 'yourPairs',
							command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/pairing`),
							label: t('userPanel.navigation.yourPairs'),
						},
						// only if program has enabled option
						(browseMembersMentorAccess || browseMembersMenteeAccess) && {
							id: 'browseMembers',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/browseMembers`),
							label:
								panelType === 'mentee'
									? t('userPanel.navigation.potentialMembers.allPairMentors')
									: t('userPanel.navigation.potentialMembers.allPairMentees'),
						},
					].filter(Boolean),
				},
				{
					id: 'divider2',
					className: 'p-panelmenu-divider',
				},
				{
					id: 'knowledge-base',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/knowledge-base`),
					label: `${t('userPanel.navigation.files')}`,
					icon: <BookSvg />,
				},
				eventsEnabled && {
					id: 'timeline',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/timeline`),
					label: `${t('userPanel.navigation.timeline')}`,
					icon: <CalendarDateSvg />,
				},
				{
					id: 'your-data',
					label: t('userPanel.navigation.yourData'),
					icon: <UserSvg />,
					expanded: false,
					items: [
						{
							id: 'personal-details',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/personal-details`),
							label: t('userPanel.navigation.personalDetails'),
							icon: <UserSvg />,
						},
						...(linkedInBadgeEnabled
							? [
									{
										id: 'linkedin-badge',
										command: () =>
											navigate(
												`/panel/${organizationName}/${programName}/${panelType}/linkedIn-badge`
											),
										label: t('userPanel.navigation.linkedInBadge'),
										icon: <BrandLinkedInSvg />,
									},
							  ]
							: []),
						{
							id: 'edit-your-data',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/application`),
							label: t('userPanel.personalDetails.editData'),
							icon: <FileEdit />,
						},
						{
							id: 'my-calendar',
							command: () => {
								setMyCalendarModalOpen(true);
							},
							label: t('userPanel.navigation.myCalendar'),
							icon: <CalendarFullSvg />,
						},
						{
							id: 'account-settings',
							command: () => setMyAccountModalOpen?.(true),
							label: t('userPanel.navigation.accountSettings'),
							icon: <SettingsSvg />,
						},
					],
				},
				{
					id: 'divider3',
					className: 'p-panelmenu-divider',
				},
				(currentProgram as any).hasContact && {
					id: 'contact',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/contact`),
					label: t('userPanel.navigation.contact'),
					icon: <CommunicationSvg />,
				},
			].filter(Boolean),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			browseMembersMenteeAccess,
			browseMembersMentorAccess,
			currentProgram,
			organizationName,
			pairsMenu,
			panelType,
			programName,
		]
	);

	const navigationBottomOptions: MenuItemExt[] = useMemo(
		() => [
			{
				id: 'programPage',
				icon: <DesctopSvg />,
				label: t('userPanel.navigation.programPage'),
				command: () => navigate(`/${organizationName}/${programName}`),
			},
			{
				id: 'reportIssue',
				icon: <HandSvg />,
				label: t('userPanel.navigation.reportIssue'),
				command: () => setReportIssueModalIsOpen?.(true),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const [surveyPopupDialogOpen, setSurveyPopupDialogOpen] = useState(false);
	useQuery(
		['FINAL_SURVEY_POPUP', { programMembershipId: Number(currentProgramMembership?.id) }],
		() => surveyApi.checkSurveyPopupForProgramMembership(Number(currentProgramMembership?.id)),
		{
			enabled: true,
			onSuccess: (displayPopup: boolean) => {
				if (displayPopup) {
					setSurveyPopupDialogOpen(true);
				}
			},
		}
	);

	const { pathname } = useLocation();
	const isPairRoute = pathname.includes('/pair/');

	return (
		<>
			<Helmet title={t('misc.pageTitles.panel')} />
			<WelcomeDialog />
			<FinalSurveyPanel
				withIntro
				visible={surveyPopupDialogOpen}
				onHide={() => setSurveyPopupDialogOpen(false)}
			/>
			<Dialog
				title={t('landingPages.buttons.changeOrganization')}
				size="sm"
				onHide={() => setShowOrgDialog(false)}
				visible={showOrgDialog}
			>
				<ChangeOrganization
					defaultSelected={
						organizationName && programName
							? {
									organization: organizationName,
									program: programName,
							  }
							: undefined
					}
					card
					onCancel={() => setShowOrgDialog(false)}
				/>
			</Dialog>
			<Dialog
				title={t('userPanel.navigation.reportIssue')}
				size="lg"
				onHide={() => setReportIssueModalIsOpen?.(false)}
				visible={Boolean(reportIssueModalIsOpen)}
			>
				<ReportIssues />
			</Dialog>

			<MyAccountDialog
				visible={myAccountModalOpen}
				onHide={() => {
					setMyAccountModalOpen(false);
				}}
			/>
			<ChangeAvatarDialog visible={avatarModalIsOpen} onHide={() => setAvatarModalIsOpen(false)} />
			<MyCalendarDialog
				visible={myCalendarModalOpen}
				onHide={() => {
					setMyCalendarModalOpen(false);
				}}
			/>

			<PanelLayout
				navigationOptions={navigationOptions}
				navigationTop={
					<div className="h-5rem p-2">
						<div className="flex flex-column align-items-end justify-content-center h-full w-full text-base text-right">
							<span>
								{t('userPanel.youAreInPanel')}{' '}
								<b>{panelType === 'mentor' ? t('misc.mentors') : t('misc.mentees')}</b>
							</span>

							{canSwitchPanels && (
								<Link
									// onClick={window.location.reload}
									to={`/panel/${organizationName}/${programName}/${
										panelType === 'mentor' ? 'mentee' : 'mentor'
									}`}
									className="text-xs underline"
									style={{ color: colors.purplishBlue }}
								>
									{`${t('userPanel.goTo')} ${
										panelType === 'mentor' ? t('misc.mentees') : t('misc.mentors')
									}`}
								</Link>
							)}
						</div>
					</div>
				}
				navigationBottom={<PanelMenu model={navigationBottomOptions} multiple />}
				header={
					<>
						<Button
							icon="fa fa-bars fa-2x"
							iconOnly
							label="Toggle menu"
							onClick={() => setVisibleLeft(true)}
							variant="primary-text"
							className="mx-2 flex lg:hidden"
						/>
						<Sidebar
							role="navigation"
							visible={visibleLeft}
							onHide={() => setVisibleLeft(false)}
							showCloseIcon={false}
							style={{
								backgroundColor: `${colors.purplishBlue}`,
								borderRadius: '0px 20px 20px 0px',
								maxWidth: '70%',
							}}
						>
							<PanelMenu
								model={navigationOptions.map((item) => ({
									...item,
									...(!isPairRoute || item.id === 'your-data'
										? {
												expanded: false,
										  }
										: {}),
								}))}
								onHide={() => setVisibleLeft(false)}
								multiple
							/>
							<Divider margin={4} />
							<PanelMenu model={navigationBottomOptions} onHide={() => setVisibleLeft(false)} multiple />
						</Sidebar>
						<div className="flex md:hidden h-5rem p-2">
							<div className="flex flex-column align-items-start justify-content-center h-full w-full text-base text-right">
								<h4 className="mt-0 mb-1">{subtitle}</h4>
								<span className="text-xs">
									{t('userPanel.youAreInPanel')}{' '}
									<b>{panelType === 'mentor' ? t('misc.mentors') : t('misc.mentees')}</b>
								</span>

								{canSwitchPanels && (
									<Link
										to={`/panel/${organizationName}/${programName}/${
											panelType === 'mentor' ? 'mentee' : 'mentor'
										}`}
										className="text-xs underline"
										style={{ color: colors.purplishBlue }}
									>
										{`${t('userPanel.goTo')} ${
											panelType === 'mentor' ? t('misc.mentees') : t('misc.mentors')
										}`}
									</Link>
								)}
							</div>
						</div>
						<img
							src={logotype || '#'}
							alt="logo"
							className="hidden md:flex max-h-3rem mr-3 cursor-pointer"
							onClick={() => navigate(`/${organizationName}/${programName}`)}
						/>
						<div className=" h-3rem pl-3 hidden md:block" style={{ borderLeft: border(colors.lightGray) }}>
							<h3 className="mt-0 mb-1">{displayName}</h3>
							<small>{subtitle}</small>
						</div>
						{currentUser && (
							<div
								className="flex align-items-center h-3rem ml-auto pr-3 pl-4 gap-2"
								style={{ borderLeft: border(colors.lightGray) }}
							>
								<div className="hidden md:flex align-items-center gap-2 mr-2">
									<span
										className="text-xs text-muted pr-1 professional-view-label"
										data-tooltip={
											currentUser?.isProfessionalViewEnabled
												? t('userPanel.settings.professionalViewTooltipOn')
												: t('userPanel.settings.professionalViewTooltipOff')
										}
									>
										{currentUser?.isProfessionalViewEnabled
											? t('userPanel.settings.professionalView')
											: t('userPanel.settings.simpleView')}
									</span>
									<InputSwitch
										checked={currentUser?.isProfessionalViewEnabled}
										onChange={() =>
											usersApi
												.editMyData({
													isProfessionalViewEnabled: !currentUser?.isProfessionalViewEnabled,
												})
												.then(() => currentUserRefetch())
										}
									/>
								</div>
								<ChatButton />
								<UserSettings
									currentUser={currentUser}
									setShowOrgDialog={setShowOrgDialog}
									optionsLanguage={i18n.resolvedLanguage}
									source="userPanel"
								/>
							</div>
						)}
					</>
				}
				right={<Tools />}
			>
				<Outlet />
			</PanelLayout>
		</>
	);
};

const UserPanelLayout = () => {
	const { panelLinkPrefix, currentApplicationData } = usePanel();
	const linkPrefix = `${panelLinkPrefix}/knowledge-base`;

	return (
		<KnowleedgeBaseContextProvider
			context="user-panel"
			useSlugReplacer
			linkPrefix={`/${linkPrefix}`}
			queries={{
				getDirectories: () => knowledgeBaseApi.application.getDirectories(currentApplicationData.id),
				getArticlesByDirectory: (directoryId: number, searchValue?: string | null) =>
					knowledgeBaseApi.application.getArticlesByDirectory(
						currentApplicationData.id,
						directoryId,
						searchValue
					),
				getArticlesByTag: (tagId: number, searchValue?: string | null) =>
					knowledgeBaseApi.application.getArticlesByTag(tagId, currentApplicationData.id, searchValue),
				getArticlesBySearch: (searchValue: string | null) =>
					knowledgeBaseApi.application.getArticlesBySearch(currentApplicationData.id, searchValue),
				getArticleById: (articleId: number, params) =>
					knowledgeBaseApi.application.getArticleById(currentApplicationData.id, articleId, params),
				getRecommendedArticles: () =>
					knowledgeBaseApi.application.getRecommendedArticles(currentApplicationData.id),
				getProgramOrganizersArticles: () =>
					knowledgeBaseApi.application.getProgramArticles(currentApplicationData.id),
			}}
		>
			<UserPanelLayoutInner />
		</KnowleedgeBaseContextProvider>
	);
};

export default UserPanelLayout;
